import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { UserPlus, Camera, Trash2, QrCode, Smartphone, Copy, Loader2, Search, CheckCircle2, XCircle, AlertTriangle, Eye } from "lucide-react";
import { DuplicatesDialog } from "@/components/DuplicatesDialog";
import { QRCodeCanvas } from "qrcode.react";
import { loadFaceModels, detectEnrollFace } from "@/lib/faceapi";
import { maskCPF, maskPhone, maskCEP, maskDate, isoFromBRDate, lookupCEP, unmask, endOfValidity } from "@/lib/masks";
import { useAuth } from "@/lib/auth";
import StudentDetail from "./StudentDetail";
import { PageHeader, TableCard, StatusBadge } from "@/components/PageHeader";

export default function Students() {
  const { user } = useAuth();
  const [students, setStudents] = useState([]);
  const [plans, setPlans] = useState([]);
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(newForm());
  const [captured, setCaptured] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [modelsReady, setModelsReady] = useState(false);
  const [inviteDialog, setInviteDialog] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [search, setSearch] = useState("");
  const [cepLoading, setCepLoading] = useState(false);
  const [cpfCheck, setCpfCheck] = useState(null);
  const cpfTimer = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  function newForm() {
    return { name: "", email: "", cpf: "", phone: "", birthdate: "", gender: "",
      plan_id: "", unit_id: "",
      zip_code: "", address: "", address_number: "", address_complement: "",
      neighborhood: "", city: "", state: "",
      goal: "", experience_level: "iniciante", portal_password: "" };
  }

  const onCpfChange = (v) => {
    const m = maskCPF(v);
    setForm((f) => ({ ...f, cpf: m }));
    setCpfCheck(null);
    if (cpfTimer.current) clearTimeout(cpfTimer.current);
    const clean = m.replace(/\D/g, "");
    if (clean.length === 11) {
      cpfTimer.current = setTimeout(async () => {
        try {
          const { data } = await api.get(`/students/check-cpf/${clean}`);
          setCpfCheck(data);
          if (data.exists) toast.error(`CPF já cadastrado para ${data.student_name}`);
        } catch {}
      }, 400);
    }
  };

  const onCepChange = async (val) => {
    const masked = maskCEP(val);
    setForm((f) => ({ ...f, zip_code: masked }));
    if (unmask(masked).length === 8) {
      setCepLoading(true);
      const found = await lookupCEP(masked);
      setCepLoading(false);
      if (found) {
        setForm((f) => ({
          ...f,
          address: found.address || f.address,
          neighborhood: found.neighborhood || f.neighborhood,
          city: found.city || f.city,
          state: found.state || f.state,
        }));
        toast.success("Endereço preenchido");
      } else {
        toast.error("CEP não encontrado");
      }
    }
  };

  const [allUnits, setAllUnits] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [situation, setSituation] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [loadingList, setLoadingList] = useState(false);
  const [dupOpen, setDupOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const showDup = async (s) => {
    try { await api.post(`/students/${s.id}/unhide-duplicate`); toast.success(`${s.name} voltou a aparecer normalmente`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const load = async () => {
    setLoadingList(true);
    const params = { page, page_size: 50, q: search.trim(), situation, unit_id: unitFilter };
    if (allUnits) params.all_units = true;
    try {
      const [s, p, u] = await Promise.all([api.get("/students", { params }), api.get("/plans"), api.get("/units")]);
      setStudents(s.data.items); setTotal(s.data.total); setPages(s.data.pages); setPlans(p.data); setUnits(u.data);
    } finally { setLoadingList(false); }
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [allUnits, page, search, situation, unitFilter]);
  useEffect(() => { setPage(1); }, [allUnits, search, situation, unitFilter]);
  useEffect(() => {
    if (open && !modelsReady) loadFaceModels().then(()=>setModelsReady(true)).catch(()=>toast.error("Falha carregando face-api"));
  }, [open, modelsReady]);

  const startCam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 640, height: 480 } });
      videoRef.current.srcObject = stream; streamRef.current = stream; setStreaming(true);
    } catch { toast.error("Sem acesso à câmera"); }
  };
  const stopCam = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    setStreaming(false);
  };
  const capture = async () => {
    if (!modelsReady) return toast.error("Modelos carregando...");
    const video = videoRef.current, canvas = canvasRef.current;
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.7);
    const desc = await detectEnrollFace(video);
    if (desc.problem) return toast.error(desc.problem);
    setCaptured({ photo: dataUrl, descriptor: desc.descriptor });
    stopCam();
    toast.success("Rosto capturado (128D)");
  };

  const save = async () => {
    if (cpfCheck?.exists) return toast.error("CPF já cadastrado — verifique antes de salvar");
    try {
      const body = { ...form };
      if (body.birthdate) body.birthdate = isoFromBRDate(body.birthdate) || body.birthdate;
      if (captured) { body.face_photo = captured.photo; body.face_embedding = captured.descriptor; }
      delete body.plan_id;
      if (!body.unit_id) delete body.unit_id;
      await api.post("/students", body);
      toast.success("Aluno cadastrado com sucesso");
      setOpen(false); setForm(newForm()); setCaptured(null); setCpfCheck(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Remover aluno?")) return;
    await api.delete(`/students/${id}`); toast.success("Removido"); load();
  };
  const invite = async (s) => {
    try {
      const { data } = await api.post(`/students/${s.id}/invite`);
      const link = `${window.location.origin}/face-enroll/${data.token}`;
      setInviteDialog({ student: s, link });
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const unitName = (id) => units.find(u => u.id === id)?.name || "—";
  const filtered = students;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Alunos"
        actions={
          <>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
              <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar por nome, CPF ou telefone" className="pl-9 w-72" data-testid="students-search"/>
              {user?.role !== "admin" && (user?.unit_id) && (
                <label className="ml-3 inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none" data-testid="students-all-units-toggle">
                  <input type="checkbox" checked={allUnits} onChange={(e)=>setAllUnits(e.target.checked)} className="accent-rose-600 w-4 h-4"/>
                  <span className="font-semibold uppercase tracking-wider">Ver todas as unidades</span>
                </label>
              )}
            </div>
            <Dialog open={open} onOpenChange={(v)=>{setOpen(v); if(!v){ stopCam(); setCaptured(null); }}}>
              <DialogTrigger asChild>
                <Button className="bg-rose-600 hover:bg-rose-700 font-bold" data-testid="student-new-button"><UserPlus className="w-4 h-4 mr-1"/>Novo Aluno</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Cadastrar aluno</DialogTitle></DialogHeader>
                <p className="text-xs text-slate-500 -mt-1" data-testid="student-new-hint">Apenas cadastro: o aluno fica Inativo, sem plano e sem acesso à catraca até a primeira venda.</p>
                <div className="grid md:grid-cols-2 gap-3 mt-3">
                  <div><Label>Nome</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} data-testid="student-name-input"/></div>
                  <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} data-testid="student-email-input"/></div>
                  <div>
                    <Label>CPF</Label>
                    <Input value={form.cpf} onChange={(e)=>onCpfChange(e.target.value)} placeholder="000.000.000-00" data-testid="student-cpf-input"
                      className={cpfCheck?.exists ? "border-rose-500 bg-rose-50" : ""}/>
                    {cpfCheck?.exists && (
                      <div className="mt-1 flex items-center gap-1 text-xs text-rose-600 font-semibold" data-testid="student-cpf-duplicate">
                        <AlertTriangle className="w-3 h-3"/>Já cadastrado: {cpfCheck.student_name}
                      </div>
                    )}
                  </div>
                  <div><Label>Telefone</Label><Input value={form.phone} onChange={(e)=>setForm({...form,phone:maskPhone(e.target.value)})} placeholder="(11) 99999-9999" data-testid="student-phone-input"/></div>
                  <div><Label>Nascimento</Label><Input inputMode="numeric" value={form.birthdate} onChange={(e)=>setForm({...form,birthdate:maskDate(e.target.value)})} placeholder="DD/MM/AAAA" data-testid="student-birthdate-input"/></div>
                  <div>
                    <Label>Gênero</Label>
                    <Select value={form.gender} onValueChange={(v)=>setForm({...form,gender:v})}>
                      <SelectTrigger data-testid="student-gender-select"><SelectValue placeholder="Selecionar"/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Masculino">Masculino</SelectItem>
                        <SelectItem value="Feminino">Feminino</SelectItem>
                        <SelectItem value="Outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2 pt-2 mt-2 border-t border-slate-200">
                    <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Endereço</div>
                    <div className="grid md:grid-cols-6 gap-3">
                      <div className="md:col-span-2">
                        <Label>CEP {cepLoading && <span className="text-xs text-sky-600 ml-1">buscando...</span>}</Label>
                        <Input value={form.zip_code} onChange={(e)=>onCepChange(e.target.value)} placeholder="00000-000" data-testid="student-cep-input"/>
                      </div>
                      <div className="md:col-span-4"><Label>Logradouro</Label><Input value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})} data-testid="student-address-input"/></div>
                      <div className="md:col-span-1"><Label>Número</Label><Input value={form.address_number} onChange={(e)=>setForm({...form,address_number:e.target.value})}/></div>
                      <div className="md:col-span-2"><Label>Complemento</Label><Input value={form.address_complement} onChange={(e)=>setForm({...form,address_complement:e.target.value})}/></div>
                      <div className="md:col-span-3"><Label>Bairro</Label><Input value={form.neighborhood} onChange={(e)=>setForm({...form,neighborhood:e.target.value})}/></div>
                      <div className="md:col-span-4"><Label>Cidade</Label><Input value={form.city} onChange={(e)=>setForm({...form,city:e.target.value})}/></div>
                      <div className="md:col-span-2"><Label>UF</Label><Input maxLength={2} value={form.state} onChange={(e)=>setForm({...form,state:e.target.value.toUpperCase()})}/></div>
                    </div>
                  </div>
                  {user?.role === "admin" && (
                    <div className="md:col-span-2"><Label>Unidade</Label>
                      <Select value={form.unit_id} onValueChange={(v)=>setForm({...form,unit_id:v})}>
                        <SelectTrigger data-testid="student-unit-select"><SelectValue placeholder="Selecionar"/></SelectTrigger>
                        <SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  <div><Label>Nível</Label>
                    <Select value={form.experience_level} onValueChange={(v)=>setForm({...form,experience_level:v})}>
                      <SelectTrigger><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="iniciante">Iniciante</SelectItem>
                        <SelectItem value="intermediario">Intermediário</SelectItem>
                        <SelectItem value="avancado">Avançado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Senha do portal</Label><Input type="password" value={form.portal_password} onChange={(e)=>setForm({...form,portal_password:e.target.value})} placeholder="opcional"/></div>
                  <div className="md:col-span-2"><Label>Objetivo</Label><Textarea rows={2} value={form.goal} onChange={(e)=>setForm({...form,goal:e.target.value})}/></div>
                </div>
                <div className="border-t border-slate-200 pt-3 mt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Camera className="w-4 h-4 text-rose-600"/><span className="font-semibold text-sm">Reconhecimento facial</span>
                    {modelsReady ? <span className="text-xs text-emerald-600">✓ pronto</span> : <span className="text-xs text-amber-600">carregando...</span>}
                  </div>
                  {captured ? (
                    <div className="flex items-center gap-3">
                      <img src={captured.photo} className="w-24 h-24 rounded-lg object-cover border-2 border-emerald-500" alt=""/>
                      <div className="text-sm text-emerald-700">Rosto capturado com descriptor 128D</div>
                      <Button size="sm" variant="outline" onClick={()=>setCaptured(null)}>Refazer</Button>
                    </div>
                  ) : streaming ? (
                    <div className="space-y-2">
                      <video ref={videoRef} autoPlay playsInline className="w-full max-w-sm rounded-lg bg-black"/>
                      <canvas ref={canvasRef} className="hidden"/>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={capture} data-testid="student-capture-face" className="bg-emerald-600 hover:bg-emerald-700">Capturar rosto</Button>
                        <Button size="sm" variant="outline" onClick={stopCam}>Cancelar</Button>
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={startCam} data-testid="student-open-cam">
                      <Camera className="w-4 h-4 mr-1"/>Abrir câmera
                    </Button>
                  )}
                </div>
                <Button onClick={save} data-testid="student-save-button" className="w-full mt-3 bg-rose-600 hover:bg-rose-700 font-bold uppercase">Salvar aluno</Button>
              </DialogContent>
            </Dialog>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2" data-testid="students-filters">
        <select value={situation} onChange={(e)=>setSituation(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="students-situation-filter">
          <option value="">Todas as situações</option><option value="active">Ativos</option><option value="inactive">Inativos</option><option value="suspended">Suspensos</option><option value="blocked">Bloqueados</option>
          {isAdmin && <option value="duplicates">Repetidos (ocultos)</option>}
        </select>
        {isAdmin && (
          <Button variant="outline" size="sm" onClick={()=>setDupOpen(true)} data-testid="students-duplicates-button" className="border-amber-300 text-amber-800 hover:bg-amber-50">
            <Copy className="w-4 h-4 mr-1"/>Nomes repetidos
          </Button>
        )}
        {(user?.role === "admin" || allUnits) && (
          <select value={unitFilter} onChange={(e)=>setUnitFilter(e.target.value)} className="h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="students-unit-filter">
            <option value="">Todas as unidades</option>{units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        {total != null && <span className="text-sm text-slate-600" data-testid="students-total">{loadingList ? "Carregando…" : `${total.toLocaleString("pt-BR")} aluno(s)`}</span>}
      </div>

      <TableCard>
        <table className="w-full min-w-[900px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
              <th className="px-5 py-3">Aluno</th>
              <th className="px-5 py-3">Unidade</th>
              <th className="px-5 py-3">Plano</th>
              <th className="px-5 py-3">Face</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody data-testid="students-list">
            {filtered.map(s => {
              const plan = plans.find(p => p.id === s.plan_id);
              const active = !!(s.membership_end && endOfValidity(s.membership_end) > new Date() && !s.blocked && (s.access_ok ?? true));
              return (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" onClick={()=>setDetailId(s.id)} data-testid="student-row">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {s.face_photo ? <img src={s.face_photo} className="w-10 h-10 rounded-full object-cover border-2 border-rose-200" alt=""/> :
                        <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-sm font-bold text-slate-600">{s.name[0]}</div>}
                      <div>
                        <div className="font-semibold text-slate-900 text-sm">{s.name}</div>
                        <div className="text-xs text-slate-500 font-mono">{s.cpf}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-slate-700">{unitName(s.unit_id)}</td>
                  <td className="px-5 py-3 text-sm text-slate-700">
                    <div>{plan?.name || "—"}</div>
                    {s.membership_end && (() => {
                      const d = String(s.membership_end).length === 10 ? new Date(`${s.membership_end}T23:59:59-03:00`) : new Date(s.membership_end);
                      const venc = d < new Date();
                      return <div className={`text-xs ${venc ? "text-rose-600 font-bold" : "text-slate-500"}`} data-testid={`student-expiry-${s.id}`}>{venc ? "Venceu em" : "Vence em"} {d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}</div>;
                    })()}
                  </td>
                  <td className="px-5 py-3">
                    {(s.has_face || s.face_embedding)
                      ? <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold"><CheckCircle2 className="w-3 h-3"/>128D</span>
                      : <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-bold"><AlertTriangle className="w-3 h-3"/>pendente</span>}
                  </td>
                  <td className="px-5 py-3">
                    {s.suspended ? <span data-testid={`student-status-suspended-${s.id}`}><StatusBadge status="suspenso"/></span> : s.blocked ? <StatusBadge status="blocked"/> : active ? <StatusBadge status="active"/> : <StatusBadge status="inactive"/>}
                  </td>
                  <td className="px-5 py-3 text-right" onClick={(e)=>e.stopPropagation()}>
                    <div className="flex items-center gap-1 justify-end">
                      {situation === "duplicates" && (
                        <Button size="sm" onClick={()=>showDup(s)} data-testid={`student-unhide-${s.id}`} className="text-xs bg-emerald-600 hover:bg-emerald-700">
                          <Eye className="w-3 h-3 mr-1"/>Ativar / mostrar
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={()=>invite(s)} data-testid="student-invite-button" className="text-xs border-rose-200 text-rose-600 hover:bg-rose-50">
                        <QrCode className="w-3 h-3 mr-1"/>Convite face
                      </Button>
                      <Button variant="ghost" size="icon" onClick={()=>remove(s.id)}><Trash2 className="w-4 h-4 text-slate-400"/></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">{search ? "Nenhum aluno encontrado" : "Nenhum aluno cadastrado"}</td></tr>
            )}
          </tbody>
        </table>
      </TableCard>
      <div className="flex items-center justify-end gap-2 text-sm" data-testid="students-pagination">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={()=>setPage(p => p - 1)} data-testid="students-prev-page">Anterior</Button>
        <span data-testid="students-page-info">{total != null ? `Página ${page} de ${pages}` : `Página ${page}`}</span>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={()=>setPage(p => p + 1)} data-testid="students-next-page">Próxima</Button>
      </div>

      <Dialog open={!!inviteDialog} onOpenChange={(v)=>{if(!v) setInviteDialog(null);}}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Smartphone className="w-5 h-5 text-rose-600"/>Convite facial — {inviteDialog?.student.name}</DialogTitle></DialogHeader>
          {inviteDialog && (
            <div className="space-y-4 mt-2">
              <p className="text-sm text-slate-600">O aluno escaneia o QR pelo celular e cadastra o rosto sozinho.</p>
              <div className="flex justify-center py-4 bg-slate-50 rounded-xl">
                <QRCodeCanvas value={inviteDialog.link} size={200} bgColor="#ffffff" fgColor="#09090B"/>
              </div>
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg text-xs font-mono break-all">
                {inviteDialog.link}
                <button onClick={()=>{navigator.clipboard.writeText(inviteDialog.link); toast.success("Link copiado");}} className="shrink-0"><Copy className="w-4 h-4 text-rose-600"/></button>
              </div>
              <Button onClick={()=>{
                const phone = (inviteDialog.student.phone || "").replace(/\D/g, "");
                const p = phone.length === 11 || phone.length === 10 ? "55" + phone : phone;
                const msg = encodeURIComponent(`Olá ${inviteDialog.student.name}! Clique aqui para cadastrar seu rosto na catraca da academia: ${inviteDialog.link}`);
                if (!p) { toast.error("Aluno sem telefone cadastrado"); return; }
                window.open(`https://wa.me/${p}?text=${msg}`, "_blank");
              }} className="w-full bg-emerald-600 hover:bg-emerald-700">Enviar via WhatsApp</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <DuplicatesDialog open={dupOpen} onClose={()=>setDupOpen(false)} onApplied={load}/>
      <StudentDetail studentId={detailId} open={!!detailId} onClose={()=>setDetailId(null)} onUpdate={load}/>
    </div>
  );
}
